import { AppData } from '@lightningjs/sdk'
import axios from 'axios'
import { AppSetup } from '../app-setup'
import { PLATFORM } from '../enum'
import { Logger } from '../logger'

// Max time an ad request waits for the bid (ms), per APS's recommended C2S timeout.
const BID_TIMEOUT = 1000

// Macro values used when no bid is available.
const EMPTY = { reg: '', brmid: '', bid: '', bids: '' }

// Bridge keys copied into the OpenRTB `device` object (`w`/`h`/`ua` are set separately).
const DEVICE_KEYS = ['dnt', 'ifa', 'make', 'language', 'model']

// Bridge key holding the APS slot id per ad context (any other context is not bid on).
const SLOT_KEYS: any = { preroll: 'slotIdPre', midroll: 'slotIdMid' }

// Sent as app.ext.inventorypartnerdomain, matching the Android implementation.
const INVENTORY_PARTNER_DOMAIN = 'futuretodayinc.com'

// Content rating -> regs.ext.agerange bucket (anything unlisted is '0').
const AGE_RANGE: any = {
    '18+': '3',
    'NC-17': '3',
    R: '3',
    'TV-MA': '3',
    '13+': '2',
    '14+': '2',
    '16+': '2',
    'PG-13': '2',
    'TV-14': '2',
}

const vega = window as any

/**
 * @description Stores the APS_BID_PARAMS payload sent by the RN shell on
 * `window.__VEGA_BRIDGE__.APS_BID_PARAMS`. Requested once at app start by
 * `PlatformFireTV.initialize()`.
 */
window.addEventListener('message', (e: any) => {
    try {
        const { type, payload } = JSON.parse(e.data)
        if (type === 'APS_BID_PARAMS' && payload) {
            vega.__VEGA_BRIDGE__ = vega.__VEGA_BRIDGE__ || {}
            vega.__VEGA_BRIDGE__.APS_BID_PARAMS = payload
        }
    } catch (err) {
        return
    }
})

/**
 * @description VegaOS (FireTV) - Amazon Publisher Services C2S header bidding.
 * One bid per cue point, shared by every ad request for that break.
 */
export default class ApsBid {
    key = ''
    pending: Promise<any> = Promise.resolve()
    values: any = EMPTY

    /**
     * @description Starts a bid for the cue point (FireTV only), unless one already exists for this key.
     * @param {string} key cue point key, `${context}:${breakPointPos}`
     * @param {string} context 'preroll' or 'midroll'
     * @param {object} vodInfo currently playing video node
     */
    start = (key: string, context: string, vodInfo: any) => {
        try {
            const { appConfig } = AppData as AppSetup
            if (appConfig?.platform_id != PLATFORM.FIRETV) return
            const params = vega.__VEGA_BRIDGE__ && vega.__VEGA_BRIDGE__.APS_BID_PARAMS
            const slotId = params && params[SLOT_KEYS[context]]
            if (!slotId || this.key === key) return
            this.key = key
            this.values = EMPTY

            if (!params.appId || !params.apsUrl) {
                Logger.printLog('data', 'APS bid skipped - bridge params unavailable', '')
                return
            }

            this.pending = this.send(this.buildRequest(slotId, params, vodInfo), key, params.apsUrl)
        } catch (e) {
            Logger.printLog('data', 'APS bid request build failed: ', e)
            this.values = EMPTY
        }
    }

    /**
     * @description Starts a bid and waits for it. For callers that build the ad URL
     * themselves (FUSE prefetch) instead of going through CuePoint.requestAds().
     * @param {string} key cue point key, `${context}:${breakPointPos}`
     * @param {string} context 'preroll' or 'midroll'
     * @param {object} vodInfo currently playing video node
     */
    request = (key: string, context: string, vodInfo: any) => {
        this.start(key, context, vodInfo)
        return this.wait(key)
    }

    /**
     * @description Resolves when the bid for this key settles, or immediately if the key is not active.
     * @param {string} key cue point key
     */
    wait = (key: string) => (this.key === key ? this.pending : Promise.resolve())

    /**
     * @description Returns the bid values for this key.
     * @param {string} key cue point key
     * @returns {object} `{reg, brmid, bid, bids}`, or EMPTY if the key is not active
     */
    get = (key: string) => (this.key === key ? this.values : EMPTY)

    /**
     * @description Builds the OpenRTB bid request body.
     * @param {string} slotId APS slot id for this ad break
     * @param {object} params bridge payload (device + account config)
     * @param {object} vodInfo currently playing video node
     * @returns {object} OpenRTB bid request
     */
    buildRequest = (slotId: string, params: any, vodInfo: any) => {
        const { appConfig, appState, platform } = AppData as AppSetup
        const device: any = {}
        for (let i = 0; i < DEVICE_KEYS.length; i++) {
            const k = DEVICE_KEYS[i]
            if (params[k] !== undefined && params[k] !== null) device[k] = params[k]
        }
        device.ua = navigator.userAgent || params.ua || ''
        device.w = Number(params.w) || 1920
        device.h = Number(params.h) || 1080
        const app = appState?.getApp()
        const appApiConfig = app?.appApiConfig
        const name =
            params.name ||
            platform?.getAppNameMacro(appApiConfig?.appConfig?.site_name, appConfig.siteId)
        const rating = (vodInfo && vodInfo['age_appropriate_rating']) || ''
        return {
            id: crypto.randomUUID(),
            app: {
                id: params.appId,
                name: name,
                domain: appApiConfig?.appConfig?.domain || params.domain,
                bundle: platform?.getAppStoreId(),
                storeurl: platform?.getAppStoreUrlMacro(name, appConfig.siteId),
                content: {
                    id: this.shuffleNodeId(vodInfo && vodInfo['node_id']),
                    contentrating: platform?.getRatingMacro(rating) || '',
                    genre:
                        platform?.getGenreMacro((vodInfo && vodInfo['content_genre']) || '') || '',
                    channel: name || '',
                    len: String((vodInfo && vodInfo['runtime']) || ''),
                    language: (vodInfo && vodInfo['content_language_iso2']) || 'en',
                    livestream: '0',
                },
                ext: { inventorypartnerdomain: INVENTORY_PARTNER_DOMAIN },
            },
            device: device,
            imp: [
                {
                    id: '1',
                    video: {
                        w: device.w,
                        h: device.h,
                        ext: { slotId: slotId },
                    },
                },
            ],
            regs: {
                ext: {
                    us_privacy: appApiConfig?.appConfig?.us_privacy || '',
                    agerange: AGE_RANGE[rating] || '0',
                },
            },
            user: { ext: { consent: '' } },
        }
    }

    /**
     * @description Posts the bid request and stores the parsed macro values. Callers wait
     * at most BID_TIMEOUT; the request itself is not cancelled, so a late response is
     * still stored for later requests on the same cue point. Failures (non-2xx,
     * network) are logged and leave the values EMPTY.
     * @param {object} body OpenRTB bid request
     * @param {string} key cue point key
     * @param {string} url APS bid endpoint
     * @returns {Promise} resolves when the response is stored, the request fails, or BID_TIMEOUT passes
     */
    send = (body: any, key: string, url: string) => {
        Logger.printLog('data', 'APS bid request: ', body)
        const call = axios
            .post(url, body, { headers: { 'Content-Type': 'application/json' } })
            .then(({ data: res }: any) => {
                if (this.key !== key) return
                const ext = (res && res.ext) || {}
                let slots: any[] = []
                const seatbid = (res && res.seatbid) || []
                for (let i = 0; i < seatbid.length; i++) {
                    const bids = seatbid[i].bid || []
                    for (let j = 0; j < bids.length; j++) {
                        const targeting = bids[j].ext && bids[j].ext.targeting
                        if (targeting && targeting.amznslots) {
                            slots = slots.concat(targeting.amznslots)
                        }
                    }
                }
                const joined = slots.join(',')
                this.values =
                    slots.length === 0
                        ? EMPTY
                        : {
                              reg: (ext.amznregion || [])[0] || '',
                              brmid: (ext.amznbrmId || [])[0] || '',
                              bid: joined,
                              bids: joined,
                          }
                Logger.printLog('data', 'APS bid response: ', this.values)
            })
            .catch((e: any) => {
                if (this.key === key) this.values = EMPTY
                Logger.printLog('data', 'APS bid failed: ', e)
            })
        const timeout = new Promise((resolve) => setTimeout(resolve, BID_TIMEOUT))
        return Promise.race([call, timeout])
    }

    /**
     * @description Obfuscates the node id for APS: keeps the prefix, shuffles the last
     * four digits and appends '0000'.
     * @param {string|number} nodeId content node id
     * @returns {string} obfuscated id
     */
    shuffleNodeId = (nodeId: any) => {
        const value = String(nodeId || '')
        if (value.length < 4) return value + '0000'
        const digits = value.slice(-4).split('')
        for (let i = digits.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1))
            const tmp = digits[i]
            digits[i] = digits[j]
            digits[j] = tmp
        }
        return value.slice(0, -4) + digits.join('') + '0000'
    }
}
